import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface OverflowTooltipProps {
  enabled?: boolean;
  content?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  as?: "div" | "span";
}

interface OverflowTooltipPosition {
  left: number;
  top: number;
  placement: "top" | "bottom";
}

export function OverflowTooltip({
  content,
  children,
  className = "",
  as = "div",
  enabled = true
}: OverflowTooltipProps) {
  const elementRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<OverflowTooltipPosition | null>(null);

  const checkOverflow = useCallback(() => {
    const element = elementRef.current;

    if (!element) {
      setIsOverflowed(false);
      return false;
    }

    const candidates = [
      element,
      ...Array.from(element.querySelectorAll<HTMLElement>("*"))
    ];

    const hasOverflow = candidates.some((candidate) => {
      if (candidate.clientWidth <= 0) {
        return false;
      }

      return candidate.scrollWidth - candidate.clientWidth > 1;
    });

    setIsOverflowed(hasOverflow);
    return hasOverflow;
  }, []);

  const updateTooltipPosition = useCallback(() => {
    const element = elementRef.current;

    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const tooltipRect = tooltipRef.current?.getBoundingClientRect();
    const tooltipWidth = tooltipRect?.width ?? 0;
    const tooltipHeight = tooltipRect?.height ?? 0;
    const viewportPadding = 8;
    const offset = 8;
    const halfTooltipWidth = tooltipWidth / 2;
    const unclampedLeft = rect.left + (rect.width / 2);
    const left = tooltipWidth > 0
      ? Math.min(
        window.innerWidth - viewportPadding - halfTooltipWidth,
        Math.max(viewportPadding + halfTooltipWidth, unclampedLeft)
      )
      : unclampedLeft;
    const canPlaceOnTop = rect.top >= tooltipHeight + offset + viewportPadding;

    setTooltipPosition({
      left,
      top: canPlaceOnTop ? rect.top - offset : rect.bottom + offset,
      placement: canPlaceOnTop ? "top" : "bottom"
    });
  }, []);

  const hideTooltip = useCallback(() => {
    setIsTooltipOpen(false);
  }, []);

  const showTooltipIfNeeded = useCallback(() => {
    const hasOverflow = checkOverflow();

    if (!enabled || !hasOverflow) {
      setIsTooltipOpen(false);
      return;
    }

    updateTooltipPosition();
    setIsTooltipOpen(true);
  }, [checkOverflow, enabled, updateTooltipPosition]);

  useLayoutEffect(() => {
    checkOverflow();

    let frameId = window.requestAnimationFrame(checkOverflow);

    const scheduleCheck = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(checkOverflow);
    };

    const element = elementRef.current;
    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(scheduleCheck)
      : null;

    if (element && resizeObserver) {
      resizeObserver.observe(element);
      Array.from(element.querySelectorAll<HTMLElement>("*")).forEach((child) => {
        resizeObserver.observe(child);
      });
    }

    window.addEventListener("resize", scheduleCheck);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", scheduleCheck);
      resizeObserver?.disconnect();
    };
  }, [checkOverflow, children, content]);

  useEffect(() => {
    if (!enabled || !isOverflowed) {
      setIsTooltipOpen(false);
    }
  }, [enabled, isOverflowed]);

  useLayoutEffect(() => {
    if (!isTooltipOpen) {
      return;
    }

    updateTooltipPosition();

    let frameId = window.requestAnimationFrame(updateTooltipPosition);

    const schedulePositionUpdate = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateTooltipPosition);
    };

    window.addEventListener("resize", schedulePositionUpdate);
    window.addEventListener("scroll", schedulePositionUpdate, true);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", schedulePositionUpdate);
      window.removeEventListener("scroll", schedulePositionUpdate, true);
    };
  }, [isTooltipOpen, updateTooltipPosition]);

  const wrappedChildren = as === "span" ? (
    <span
      ref={elementRef as React.RefObject<HTMLSpanElement>}
      className={className}
      onMouseEnter={showTooltipIfNeeded}
      onMouseLeave={hideTooltip}
      onFocus={showTooltipIfNeeded}
      onBlur={hideTooltip}
    >
      {children}
    </span>
  ) : (
    <div
      ref={elementRef as React.RefObject<HTMLDivElement>}
      className={className}
      onMouseEnter={showTooltipIfNeeded}
      onMouseLeave={hideTooltip}
      onFocus={showTooltipIfNeeded}
      onBlur={hideTooltip}
    >
      {children}
    </div>
  );

  return (
    <>
      {wrappedChildren}
      {isTooltipOpen && tooltipPosition && createPortal(
        <div
          ref={tooltipRef}
          role="tooltip"
          className="pointer-events-none z-[999] rounded-lg bg-black px-3 py-1.5 font-sans text-sm font-normal text-white outline-none break-words whitespace-normal"
          style={{
            position: "fixed",
            left: tooltipPosition.left,
            top: tooltipPosition.top,
            transform: tooltipPosition.placement === "top"
              ? "translate(-50%, -100%)"
              : "translateX(-50%)"
          }}
        >
          {content}
        </div>,
        document.body
      )}
    </>
  );
}
